import React, { useState, useEffect } from 'react';
import { FaUsers, FaBell, FaDownload, FaHistory, FaTrash, FaToggleOn, FaEnvelope, FaFileExport, FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import useBulkOperations from '../hooks/useBulkOperations';
import { useToast } from '../components/ToastContainer';
import '../styles/BulkOperations.css';

const BulkOperations = () => {
  const {
    loading,
    error,
    updateUsersStatus,
    deleteUsers,
    sendBulkNotifications,
    exportData,
    fetchOperationsHistory
  } = useBulkOperations();

  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState('users');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // États pour les opérations
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [statusOperation, setStatusOperation] = useState({
    newStatus: 'active',
    showConfirm: false
  });
  
  const [deleteOperation, setDeleteOperation] = useState({
    showConfirm: false
  });

  const [notificationOperation, setNotificationOperation] = useState({
    title: '',
    message: '',
    type: 'system',
    showModal: false
  });

  const [exportOperation, setExportOperation] = useState({
    entityType: 'users',
    format: 'csv',
    filters: {},
    showModal: false
  });

  // Charger l'historique au montage
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetchOperationsHistory(1, 50);
      setHistory(response.operations || []);
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Gestionnaires d'opérations
  const handleStatusUpdate = async () => {
    try {
      if (selectedUsers.length === 0) {
        showToast('Veuillez sélectionner des utilisateurs', 'warning');
        return;
      }

      const response = await updateUsersStatus(selectedUsers, statusOperation.newStatus);
      showToast(`Statut mis à jour pour ${response.total_updated} utilisateur(s)`, 'success');
      setStatusOperation({ ...statusOperation, showConfirm: false });
      setSelectedUsers([]);
      loadHistory();
    } catch (error) {
      showToast('Erreur lors de la mise à jour des statuts', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedUsers.length === 0) {
        showToast('Veuillez sélectionner des utilisateurs', 'warning');
        return;
      }

      const response = await deleteUsers(selectedUsers, true);
      showToast(`${response.total_deleted} utilisateur(s) supprimé(s)`, 'success');
      setDeleteOperation({ showConfirm: false });
      setSelectedUsers([]);
      loadHistory();
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleSendNotifications = async () => {
    try {
      if (selectedUsers.length === 0) {
        showToast('Veuillez sélectionner des destinataires', 'warning');
        return;
      }

      if (!notificationOperation.title || !notificationOperation.message) {
        showToast('Titre et message requis', 'warning');
        return;
      }

      const response = await sendBulkNotifications(
        selectedUsers,
        notificationOperation.title,
        notificationOperation.message,
        notificationOperation.type
      );
      
      showToast(`Notification envoyée à ${response.notifications_sent} utilisateur(s)`, 'success');
      setNotificationOperation({
        title: '',
        message: '',
        type: 'system',
        showModal: false
      });
      setSelectedUsers([]);
      loadHistory();
    } catch (error) {
      showToast('Erreur lors de l\'envoi des notifications', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportData(
        exportOperation.entityType,
        exportOperation.filters,
        exportOperation.format
      );

      // Créer et télécharger le fichier
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_${exportOperation.entityType}_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);

      showToast(`Export de ${response.total_records} enregistrement(s) réussi`, 'success');
      setExportOperation({ ...exportOperation, showModal: false });
      loadHistory();
    } catch (error) {
      showToast('Erreur lors de l\'export', 'error');
    }
  };

  // Utilitaires
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const getOperationIcon = (action) => {
    const icons = {
      'bulk_status_update': FaToggleOn,
      'bulk_delete': FaTrash,
      'bulk_send': FaBell,
      'bulk_export': FaDownload
    };
    const IconComponent = icons[action] || FaClock;
    return <IconComponent />;
  };

  const getOperationLabel = (action) => {
    const labels = {
      'bulk_status_update': 'Mise à jour de statuts',
      'bulk_delete': 'Suppression en lot',
      'bulk_send': 'Envoi de notifications',
      'bulk_export': 'Export de données'
    };
    return labels[action] || action;
  };

  return (
    <div className="bulk-operations-page">
      {/* Header */}
      <div className="operations-header">
        <div className="header-left">
          <FaUsers className="page-icon" />
          <div>
            <h1>Opérations en Lot</h1>
            <p>Effectuer des actions sur plusieurs éléments simultanément</p>
          </div>
        </div>
      </div>

      {/* Navigation des onglets */}
      <div className="operations-tabs">
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <FaUsers />
          Gestion Utilisateurs
        </button>
        <button
          className={`tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          <FaBell />
          Notifications
        </button>
        <button
          className={`tab ${activeTab === 'export' ? 'active' : ''}`}
          onClick={() => setActiveTab('export')}
        >
          <FaFileExport />
          Export de Données
        </button>
        <button
          className={`tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <FaHistory />
          Historique
        </button>
      </div>

      <div className="operations-content">
        {/* Onglet Gestion Utilisateurs */}
        {activeTab === 'users' && (
          <div className="users-operations">
            <div className="operation-card">
              <div className="card-header">
                <FaToggleOn className="card-icon" />
                <h3>Modifier le Statut</h3>
              </div>
              <div className="card-content">
                <p>Modifier le statut de plusieurs utilisateurs simultanément</p>
                <div className="form-group">
                  <label>Nouveau statut :</label>
                  <select
                    value={statusOperation.newStatus}
                    onChange={(e) => setStatusOperation({ ...statusOperation, newStatus: e.target.value })}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                    <option value="suspended">Suspendu</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>IDs utilisateurs (un par ligne) :</label>
                  <textarea
                    value={selectedUsers.join('\n')}
                    onChange={(e) => setSelectedUsers(e.target.value.split('\n').filter(id => id.trim()))}
                    placeholder="Collez les identifiants UUID des utilisateurs ici..."
                    rows="5"
                  />
                </div>
                <button
                  className="btn-primary"
                  onClick={() => setStatusOperation({ ...statusOperation, showConfirm: true })}
                  disabled={loading || selectedUsers.length === 0}
                >
                  Mettre à jour {selectedUsers.length} utilisateur(s)
                </button>
              </div>
            </div>

            <div className="operation-card danger">
              <div className="card-header">
                <FaTrash className="card-icon" />
                <h3>Supprimer des Utilisateurs</h3>
              </div>
              <div className="card-content">
                <p className="danger-text">⚠️ Cette action est irréversible</p>
                <div className="form-group">
                  <label>IDs utilisateurs à supprimer :</label>
                  <textarea
                    value={selectedUsers.join('\n')}
                    onChange={(e) => setSelectedUsers(e.target.value.split('\n').filter(id => id.trim()))}
                    placeholder="Identifiants UUID des utilisateurs à supprimer..."
                    rows="3"
                  />
                </div>
                <button
                  className="btn-danger"
                  onClick={() => setDeleteOperation({ showConfirm: true })}
                  disabled={loading || selectedUsers.length === 0}
                >
                  <FaTrash />
                  Supprimer {selectedUsers.length} utilisateur(s)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Notifications */}
        {activeTab === 'notifications' && (
          <div className="notification-operations">
            <div className="operation-card">
              <div className="card-header">
                <FaBell className="card-icon" />
                <h3>Envoyer une Notification</h3>
              </div>
              <div className="card-content">
                <div className="form-group">
                  <label>Destinataires (IDs utilisateurs) :</label>
                  <textarea
                    value={selectedUsers.join('\n')}
                    onChange={(e) => setSelectedUsers(e.target.value.split('\n').filter(id => id.trim()))}
                    placeholder="Identifiants des destinataires..."
                    rows="4"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Titre :</label>
                    <input
                      type="text"
                      value={notificationOperation.title}
                      onChange={(e) => setNotificationOperation({ ...notificationOperation, title: e.target.value })}
                      placeholder="Titre de la notification"
                      maxLength="255"
                    />
                  </div>
                  <div className="form-group">
                    <label>Type :</label>
                    <select
                      value={notificationOperation.type}
                      onChange={(e) => setNotificationOperation({ ...notificationOperation, type: e.target.value })}
                    >
                      <option value="system">Système</option>
                      <option value="info">Information</option>
                      <option value="warning">Avertissement</option>
                      <option value="promotion">Promotion</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Message :</label>
                  <textarea
                    value={notificationOperation.message}
                    onChange={(e) => setNotificationOperation({ ...notificationOperation, message: e.target.value })}
                    placeholder="Contenu de la notification..."
                    rows="4"
                    maxLength="1000"
                  />
                  <small>{notificationOperation.message.length}/1000 caractères</small>
                </div>
                <button
                  className="btn-primary"
                  onClick={handleSendNotifications}
                  disabled={loading || selectedUsers.length === 0 || !notificationOperation.title || !notificationOperation.message}
                >
                  <FaEnvelope />
                  Envoyer à {selectedUsers.length} destinataire(s)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Export */}
        {activeTab === 'export' && (
          <div className="export-operations">
            <div className="operation-card">
              <div className="card-header">
                <FaFileExport className="card-icon" />
                <h3>Exporter des Données</h3>
              </div>
              <div className="card-content">
                <div className="form-row">
                  <div className="form-group">
                    <label>Type de données :</label>
                    <select
                      value={exportOperation.entityType}
                      onChange={(e) => setExportOperation({ ...exportOperation, entityType: e.target.value })}
                    >
                      <option value="users">Utilisateurs</option>
                      <option value="clients">Clients</option>
                      <option value="prestataires">Prestataires</option>
                      <option value="missions">Missions</option>
                      <option value="transactions">Transactions</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Format :</label>
                    <select
                      value={exportOperation.format}
                      onChange={(e) => setExportOperation({ ...exportOperation, format: e.target.value })}
                    >
                      <option value="json">JSON</option>
                      <option value="csv">CSV</option>
                      <option value="xlsx">Excel</option>
                    </select>
                  </div>
                </div>
                <p className="info-text">
                  L'export sera limité à 10 000 enregistrements maximum
                </p>
                <button
                  className="btn-primary"
                  onClick={handleExport}
                  disabled={loading}
                >
                  <FaDownload />
                  {loading ? 'Export en cours...' : `Exporter ${exportOperation.entityType}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Historique */}
        {activeTab === 'history' && (
          <div className="history-operations">
            <div className="operation-card">
              <div className="card-header">
                <FaHistory className="card-icon" />
                <h3>Historique des Opérations</h3>
                <button className="btn-secondary" onClick={loadHistory} disabled={historyLoading}>
                  {historyLoading ? 'Chargement...' : 'Actualiser'}
                </button>
              </div>
              <div className="card-content">
                {history.length === 0 ? (
                  <p className="empty-text">Aucune opération en lot effectuée récemment</p>
                ) : (
                  <div className="history-list">
                    {history.map((operation) => (
                      <div key={operation.id} className="history-item">
                        <div className="operation-info">
                          <div className="operation-icon">
                            {getOperationIcon(operation.action)}
                          </div>
                          <div className="operation-details">
                            <h4>{getOperationLabel(operation.action)}</h4>
                            <p className="operation-meta">
                              Par {operation.user_email} • {formatDate(operation.created_at)}
                            </p>
                            {operation.new_values && (
                              <div className="operation-data">
                                <pre>{JSON.stringify(JSON.parse(operation.new_values), null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="operation-status">
                          <FaCheckCircle className="status-success" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modales de confirmation */}
      {statusOperation.showConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirmer la mise à jour</h3>
            <p>Êtes-vous sûr de vouloir modifier le statut de {selectedUsers.length} utilisateur(s) ?</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setStatusOperation({ ...statusOperation, showConfirm: false })}>
                Annuler
              </button>
              <button className="btn-primary" onClick={handleStatusUpdate} disabled={loading}>
                {loading ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteOperation.showConfirm && (
        <div className="modal-overlay">
          <div className="modal danger">
            <h3><FaExclamationTriangle /> Confirmation de suppression</h3>
            <p><strong>⚠️ Cette action est irréversible !</strong></p>
            <p>Vous êtes sur le point de supprimer {selectedUsers.length} utilisateur(s) définitivement.</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteOperation({ showConfirm: false })}>
                Annuler
              </button>
              <button className="btn-danger" onClick={handleDelete} disabled={loading}>
                {loading ? 'Suppression...' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkOperations;